import Hidden from 'raydiant-elements/layout/Hidden';
import { FC, useCallback, useState, useEffect, useRef, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import logger from '../../../../../logger';
import miraClient from '../../../../../clients/miraClient';
import * as F from '../../../../../clients/mira/types/Folder';
import * as D from '../../../../../clients/mira/types/Device';
import * as R from '../../../../../clients/mira/types/Resource';
import {
  getResourceIdsFromNodeIds,
  SortFolderOptions,
} from '../../../../../utilities';
import {
  selectIsManageMode,
  selectLayoutOptions,
  selectMainSortOptions,
  selectSelectedNodeIds,
} from '../../../selectors';
import * as actions from '../../../actions';
import { useDebounce } from 'use-debounce';
import { useHistory, useLocation, useRouteMatch } from 'react-router-dom';
import * as paths from '../../../../../routes/paths';
import Button from '@raydiant/raydial/components/Button';
import {
  ArrowDownArrowUp,
  CircleArrowRight,
  Filter,
  List,
  RectangleHistory,
  Search,
  Trash,
  XMark,
} from '@raydiant/raydial/components/Icons';
import cn from 'classnames';
import Popover from '@raydiant/raydial/components/Popover';
import Dialog from '@raydiant/raydial/components/Dialog';
import { GridIcon } from '../../../../AddContentPage/AddContentActionBarV2';
import BatchDeleteContentDialog from '../DeleteContentDialog/BatchDeleteContentDialog';

interface LibraryMainActionBarProps {
  folder?: F.Folder | F.VirtualFolder;
  selectedProfile?: R.ResourceProfile;
  sortDisabled?: boolean;
  manageDisabled?: boolean;
  moveContentDisabled?: boolean;
  deleteContentDisabled?: boolean;
  isLoading?: boolean;
  onManageMode: (isManageMode: boolean) => void;
  onMove: () => void;
  onDelete: () => void;
  onCreate: () => void;
}

const SEARCH_DEBOUNCE_TIMEOUT = 1000; //in ms

const LibraryPageActionBar: FC<LibraryMainActionBarProps> = ({
  folder,
  selectedProfile,
  sortDisabled,
  manageDisabled,
  moveContentDisabled,
  deleteContentDisabled,
  isLoading = false,
  onManageMode,
  onMove,
  onDelete,
  onCreate,
}) => {
  const initialSearch =
    new URLSearchParams(window.location.search).get('search') || '';

  const profilesMatch = useRouteMatch({
    path: paths.libraryUsersLibraries.pattern,
  });

  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const previousPathRef = useRef<string | null>(null);
  const triggerRef = useRef(null);

  const selectedNodeIds = useSelector(selectSelectedNodeIds);
  const sortOptions = useSelector(selectMainSortOptions);
  const layoutOptions = useSelector(selectLayoutOptions);
  const isManageMode = useSelector(selectIsManageMode);

  const [isSortMode, setIsSortMode] = useState(false);
  const [isSearchMode, setIsSearchMode] = useState(false);
  const [searchQuery, setSearchQuery] = useState(initialSearch);
  const [affectedDevices, setAffectedDevices] = useState<D.AffectedDevice[]>(
    [],
  );
  const [affectedDevicesStatus, setAffectedDevicesStatus] = useState<
    '' | 'pending' | 'success' | 'failed'
  >('');
  const [isDeletePromptOpen, setIsDeletePromptOpen] = useState(false);

  const [debouncedSearchQuery] = useDebounce(
    searchQuery,
    SEARCH_DEBOUNCE_TIMEOUT,
  );

  const queryParams = useMemo(() => {
    const searchParams = new URLSearchParams(location.search);
    return {
      profile_id: searchParams.get('profile_id') ?? '',
    };
  }, [location.search]);

  const toggleSort = useCallback(
    (property: SortFolderOptions['property']) => {
      let direction: SortFolderOptions['direction'] =
        property === 'updatedAt' ? 'desc' : 'asc';

      if (property === sortOptions.property) {
        direction = sortOptions.direction === 'asc' ? 'desc' : 'asc';
      }

      dispatch(actions.setMainSortOptions({ property, direction }));
      setIsSortMode(false);
    },
    [dispatch, sortOptions],
  );

  const toggleLayoutOptions = useCallback(() => {
    dispatch(actions.toggleLayoutOptions());
  }, [dispatch]);

  const toggleManageMode = useCallback(() => {
    dispatch(actions.setManageMode(!isManageMode));
  }, [dispatch, isManageMode]);

  const onDeleteClick = useCallback(() => {
    onDelete();
    setIsDeletePromptOpen(false);
  }, [onDelete]);

  useEffect(() => {
    const unlisten = history.listen((location) => {
      if (location.pathname !== paths.librarySearch()) {
        previousPathRef.current = null;
      }
    });

    return () => {
      unlisten();
    };
  }, [history]);

  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const currentSearch = urlParams.get('search');
    const backTo = urlParams.get('backTo');

    if (debouncedSearchQuery.trim()) {
      if (!backTo) {
        const currentFullPath = `${location.pathname}${location.search}`;
        urlParams.set('backTo', encodeURIComponent(currentFullPath));
      }
      if (currentSearch !== debouncedSearchQuery) {
        urlParams.set('search', debouncedSearchQuery);
      }
      history.replace({
        pathname: paths.librarySearch(),
        search: urlParams.toString(),
      });
    } else {
      if (backTo) {
        const backToPath = decodeURIComponent(backTo);
        history.push(backToPath);
      }
    }
  }, [debouncedSearchQuery, history, location.pathname, location.search]);

  const deleteOrPromptIfAffectedDevices = useCallback(async () => {
    setAffectedDevices([]);
    setAffectedDevicesStatus('pending');

    try {
      const affectedDevices = await miraClient.getAffectedDevices(
        getResourceIdsFromNodeIds(selectedNodeIds),
      );

      setAffectedDevices(affectedDevices);
      setAffectedDevicesStatus('success');
      setIsDeletePromptOpen(true);
    } catch (err: any) {
      logger.error(err);
      setAffectedDevicesStatus('failed');
    }
  }, [selectedNodeIds]);

  const toggleManageMultiple = useCallback(() => {
    toggleManageMode();
    setIsSortMode(false);
    if (isManageMode) {
      dispatch(actions.setSelectedNodeIds([]));
    }
  }, [toggleManageMode, isManageMode, dispatch]);

  useEffect(() => {
    if (!isSearchMode) setSearchQuery('');
  }, [isSearchMode]);

  useEffect(() => {
    onManageMode(isManageMode);
  }, [onManageMode, isManageMode]);

  let sortLabel = 'Sort';
  if (sortOptions.property === 'name') {
    sortLabel = `${sortLabel}: Name`;
  } else if (sortOptions.property === 'resourceType') {
    sortLabel = `${sortLabel}: Type`;
  } else if (sortOptions.property === 'updatedAt') {
    sortLabel = `${sortLabel}: Date`;
  }

  const isSortByName = sortOptions.property === 'name';
  const isSortByResourceType = sortOptions.property === 'resourceType';
  const isSortByDate = sortOptions.property === 'updatedAt';
  const isReverseSort = sortOptions.direction === 'desc';
  const isProfilesPage = !!profilesMatch;

  const sortActionOptions = (
    <div className="flex flex-col text-base font-regular text-gray-700 gap-1">
      <Button
        variant="unstyled"
        fullWidth
        className={cn(
          'flex items-center px-4 py-2.5 hover:bg-gray-100',
          isSortByName && 'bg-gray-200 hover:bg-gray-200',
        )}
        onPress={() => toggleSort('name')}
      >
        {isSortByName && isReverseSort ? (
          <ArrowDownArrowUp />
        ) : (
          <ArrowDownArrowUp transform="scale(-1,1)" />
        )}
        <span className="pl-3">Name</span>
      </Button>
      <Button
        variant="unstyled"
        fullWidth
        className={cn(
          'flex items-center px-4 py-2.5 hover:bg-gray-100',
          isSortByResourceType && 'bg-gray-200 hover:bg-gray-200',
        )}
        isDisabled={isProfilesPage && !queryParams.profile_id}
        onPress={() => toggleSort('resourceType')}
      >
        {isSortByDate && isReverseSort ? (
          <ArrowDownArrowUp />
        ) : (
          <ArrowDownArrowUp transform="scale(-1,1)" />
        )}
        <span className="pl-3">Type</span>
      </Button>
      <Button
        variant="unstyled"
        fullWidth
        className={cn(
          'flex items-center px-4 py-2.5 hover:bg-gray-100',
          isSortByDate && 'bg-gray-200 hover:bg-gray-200',
        )}
        isDisabled={isProfilesPage && !queryParams.profile_id}
        onPress={() => toggleSort('updatedAt')}
      >
        {isSortByDate && isReverseSort ? (
          <ArrowDownArrowUp />
        ) : (
          <ArrowDownArrowUp transform="scale(-1,1)" />
        )}
        <span className="pl-3">Date</span>
      </Button>
    </div>
  );

  const sortAction = (
    <>
      <Button
        variant="unstyled"
        className={cn(
          'text-lg font-medium text-gray-400 hover:text-blue-600 outline-none md:w-[114px]',
          isSortMode && 'text-blue-600',
        )}
        onPress={() => setIsSortMode(true)}
      >
        <div className="flex items-center" ref={triggerRef}>
          <Filter />
          <span className="hidden pl-2.5 md:block">{sortLabel}</span>
        </div>
      </Button>
      <Popover
        triggerRef={triggerRef}
        isOpen={isSortMode}
        onOpenChange={setIsSortMode}
        className="w-[116px]"
        placement="bottom start"
      >
        <Dialog className="px-0 py-2.5" aria-label="sort options">
          <div className="overflow-hidden">{sortActionOptions}</div>
        </Dialog>
      </Popover>
    </>
  );

  const manageMultipleAction = (
    <Button
      variant="unstyled"
      className="flex items-center text-lg font-medium text-gray-400 outline-none hover:text-blue-600 md:w-[162px]"
      onPress={toggleManageMultiple}
      isDisabled={manageDisabled}
    >
      {isManageMode ? <XMark /> : <RectangleHistory />}
      <span className="hidden pl-2.5 md:block">
        {isManageMode ? 'Done' : 'Manage Multiple'}
      </span>
    </Button>
  );

  const searchAction = (
    <>
      {isSearchMode ? (
        <div className="flex text-lg font-medium text-gray-400">
          <input
            autoFocus
            className="outline-none border-b-2 max-w-[140px] mr-1"
            type="text"
            placeholder="Search"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          <Button
            variant="unstyled"
            className="text-gray-400 hover:text-blue-600 outline-none"
            onPress={() => setIsSearchMode(false)}
          >
            <XMark size={12} />
          </Button>
        </div>
      ) : (
        <Button
          variant="unstyled"
          className="flex items-center text-lg font-medium text-gray-400 enabled:hover:text-blue-600 outline-none"
          onPress={() => setIsSearchMode(true)}
        >
          <Search size={17} />
        </Button>
      )}
    </>
  );

  const layoutAction = (
    <div className="w-6 flex justify-end">
      <Button
        variant="unstyled"
        className="flex items-center text-lg font-medium text-gray-400 enabled:hover:text-blue-600 outline-none"
        onPress={() => toggleLayoutOptions()}
      >
        {layoutOptions === 'list' ? <GridIcon /> : <List size={16} />}
      </Button>
    </div>
  );

  const disableMove = moveContentDisabled || selectedNodeIds.length === 0;
  const moveContentAction = (
    <Button
      variant="unstyled"
      className="flex items-center text-gray-400 text-lg font-medium outline-none hover:text-blue-600"
      onPress={onMove}
      isDisabled={disableMove}
    >
      <CircleArrowRight />
      <span className="hidden pl-2.5 md:block">Move</span>
    </Button>
  );

  const disableDelete =
    selectedNodeIds.length === 0 ||
    affectedDevicesStatus === 'pending' ||
    deleteContentDisabled;

  const deleteAction = (
    <Button
      variant="unstyled"
      className="flex items-center text-gray-400 text-lg font-medium outline-none hover:text-blue-600"
      onPress={deleteOrPromptIfAffectedDevices}
      isDisabled={disableDelete}
    >
      <Trash />
      <span className="hidden pl-2.5 md:block">Delete</span>
    </Button>
  );

  const renderDesktopActionBar = () => (
    <>
      <div className="flex gap-6">
        {sortAction}
        {isManageMode && moveContentAction}
        {isManageMode && deleteAction}
        {manageMultipleAction}
      </div>
      <div className="grow" />
      <div className="flex gap-2">
        {searchAction}
        {layoutAction}
      </div>
    </>
  );

  const renderMobileActionBar = () => (
    <>
      <div className="flex flex-1 justify-between mr-2">
        {sortAction}
        {isManageMode && moveContentAction}
        {isManageMode && deleteAction}
        {manageMultipleAction}
        {layoutAction}
      </div>
      <div className="min-w-36 flex justify-end">{searchAction}</div>
    </>
  );

  return (
    <div className="flex bg-white px-4 rounded-3xl h-10 items-center">
      <Hidden smDown>{renderDesktopActionBar()}</Hidden>
      <Hidden mdUp>{renderMobileActionBar()}</Hidden>
      <BatchDeleteContentDialog
        isOpen={isDeletePromptOpen}
        affectedDevices={affectedDevices}
        isLoading={isLoading}
        onClose={() => setIsDeletePromptOpen(false)}
        onSubmit={onDeleteClick}
      />
    </div>
  );
};

export default LibraryPageActionBar;
